import React, { useState, useMemo } from 'react';
import { SUPABASE_URL } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Calendar, MapPin, Clock, FileSpreadsheet, LayoutGrid, CalendarDays, FileText } from 'lucide-react';
import { writeExcelFile, jsonToSheet, type ExcelSheet } from '@/utils/excelExport';

import BestFullMoons from '@/components/moon/BestFullMoons';
import { MoonCalendarTable } from '@/components/moon/MoonCalendarTable';
import { MoonCalendarLegend } from '@/components/moon/MoonCalendarLegend';
import { MoonHowToUse } from '@/components/moon/MoonHowToUse';
import { MoonYearlyCalendarView } from '@/components/moon/MoonYearlyCalendarView';
import { generateMonthlyMoonData, getBestFullMoons, MoonDayData } from '@/utils/moonCalculations';
import { LocationDisplayCard } from './LocationDisplayCard';
import { MonthlyWeatherSummary } from '@/components/weather/MonthlyWeatherSummary';
import { useLocationManagement } from '@/hooks/useLocationManagement';
import { useAuth } from '@/contexts/AuthContext';
import { getLocationParts, getTimezone } from '@/utils/timeFormatting';
import { trackExportDownload } from '@/utils/analytics';
import { useToast } from '@/hooks/use-toast';
import CalendarImportGuideModal from '../CalendarImportGuideModal';
import { edgeFunctionHeaders } from '@/lib/edgeFunctionHeaders';

interface Location {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string;
  created_at?: string;
}

interface MoonCalendarViewProps {
  selectedLocation: Location;
  hideLocationCard?: boolean;
}

const availableYears = [2025, 2026, 2027, 2028, 2029];

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const shortMonthNames = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const MoonCalendarView: React.FC<MoonCalendarViewProps> = ({ selectedLocation, hideLocationCard = false }) => {
  const { user, isSubscribed } = useAuth();
  const { toast } = useToast();
  const [viewType, setViewType] = useState<'month' | 'year'>('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isImportGuideOpen, setIsImportGuideOpen] = useState(false);
  const [downloadedFileName, setDownloadedFileName] = useState<string>('');

  const {
    isFavoriteDialogOpen,
    setIsFavoriteDialogOpen,
    favoriteFormData,
    setFavoriteFormData,
    handleStarLocation,
    refreshTrigger
  } = useLocationManagement(selectedLocation);

  // Generate moon data for selected month
  const monthData = useMemo(() => {
    return generateMonthlyMoonData(
      selectedYear,
      selectedMonth,
      selectedLocation.latitude,
      selectedLocation.longitude
    );
  }, [selectedYear, selectedMonth, selectedLocation.latitude, selectedLocation.longitude]);

  // Get best full moons
  const bestFullMoons = useMemo(() => {
    return getBestFullMoons(monthData);
  }, [monthData]);

  // Count events for calendar export
  const goodMoonNightsCount = useMemo(() => {
    return monthData.filter(day => day.illumination >= 80).length;
  }, [monthData]);

  const locationParts = getLocationParts(selectedLocation.name);
  const timezone = getTimezone(selectedLocation.latitude, selectedLocation.longitude);

  const formatLocationName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };

  const generateFilename = (format: string) => {
    const locationFormatted = formatLocationName(selectedLocation.name);
    const monthName = monthNames[selectedMonth].toLowerCase();
    return `moon-planner-${locationFormatted}-${monthName}-${selectedYear}.${format}`;
  };

  const exportToExcel = async () => {
    const filename = generateFilename('xlsx');
    trackExportDownload('xlsx', selectedLocation.name);

    const sheets: ExcelSheet[] = [];

    // Create the summary/info tab first
    const summaryData = [
      ['MOON PLANNER - CALENDAR EXPORT'],
      [''],
      ['LOCATION INFORMATION'],
      [`Location: ${selectedLocation.name}`],
      [`Coordinates: ${selectedLocation.latitude.toFixed(4)}°N, ${selectedLocation.longitude.toFixed(4)}°E`],
      [`Month: ${monthNames[selectedMonth]} ${selectedYear}`],
      [''],
      ['HOW TO USE THIS PLANNER'],
      [''],
      ['Best Moon Photography:'],
      ['Unlike Milky Way photography, moon photography thrives when the moon is'],
      ['bright and high in the sky. Full moons provide the best detail and lighting.'],
      [''],
      ['Full Moon is Your Target:'],
      ['The best lunar photography happens during full moon phases (90%+ illumination).'],
      ['Supermoons appear larger and brighter, making them especially spectacular.'],
      [''],
      ['Golden & Blue Hour Magic:'],
      ['Shooting the moon during twilight hours creates stunning compositions'],
      ['with colorful skies. Look for days with golden/blue hour overlap.'],
      [''],
      ['Pro Tips:'],
      ['• Plan shoots during full moon phases (90%+ illumination)'],
      ['• Use telephoto lenses (200mm+) for detailed lunar shots'],
      ['• Shoot during moonrise/moonset for dramatic compositions'],
      ['• Capture during twilight to include foreground elements'],
      ['• Higher altitude = clearer shots with less atmospheric haze'],
      ['• Supermoons appear ~14% larger than regular full moons'],
      [''],
      ['Legend:'],
      ['• Illumination: Percentage of moon surface lit (100% = Full Moon)'],
      ['• Max Altitude: Highest point the moon reaches in the sky'],
      ['• Supermoon: Full moon occurring near lunar perigee (appears larger)'],
      [''],
      ['Generated by MilkyWayPlanner.com']
    ];

    sheets.push({ name: 'MOON PLANNER', data: summaryData });

    // Monthly data
    const formattedData = monthData.map(day => {
      return {
        'Date': day.fullDate,
        'Moonrise': day.moonrise,
        'Moonrise Dir': day.moonriseAzimuth,
        'Moonset': day.moonset,
        'Moonset Dir': day.moonsetAzimuth,
        'Max Altitude': `${day.maxAltitude}°`,
        'Max Alt Time': day.maxAltitudeTime,
        'Illumination': `${day.illumination}%`,
        'Phase': day.moonPhaseName,
        'Supermoon': day.isSupermoon ? 'Yes' : '',
        'Twilight Note': day.twilightNote
      };
    });

    const monthlySheet = jsonToSheet(formattedData);
    monthlySheet.name = `${monthNames[selectedMonth]} ${selectedYear}`;
    sheets.push(monthlySheet);

    // Best nights for moon photography
    const bestNights = monthData
      .filter(day => day.illumination >= 80)
      .sort((a, b) => b.illumination - a.illumination)
      .map((day, index) => ({
        'Rank': `#${index + 1}`,
        'Date': day.fullDate,
        'Illumination': `${day.illumination}%`,
        'Phase': day.moonPhaseName,
        'Max Altitude': `${day.maxAltitude}°`,
        'Supermoon': day.isSupermoon ? '⭐ Yes' : '',
        'Twilight': day.twilightNote || 'Night only'
      }));

    if (bestNights.length > 0) {
      const bestNightsSheet = jsonToSheet(bestNights);
      bestNightsSheet.name = 'BEST NIGHTS';
      sheets.push(bestNightsSheet);
    }

    await writeExcelFile(sheets, filename);
  };

  const exportToICS = async () => {
    const filename = generateFilename('ics');
    setDownloadedFileName(filename);
    trackExportDownload('ics', selectedLocation.name);
    
    // Filter for good moon photography nights (80%+ illumination)
    const goodNights = monthData.filter(day => day.illumination >= 80);
    
    if (goodNights.length === 0) {
      toast({
        title: "No events to export",
        description: "No good moon photography nights found for the selected month. Try a different month.",
        variant: "destructive"
      });
      return;
    }

    const formatDateForICS = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const formatDateTimeForICS = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };

    const escapeICSText = (text: string) => {
      return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
    };

    const createICSEvent = (day: MoonDayData) => {
      const starRating = day.isSupermoon ? '🌕⭐ SUPERMOON' : '🌕';
      
      const eventDate = new Date(selectedYear, selectedMonth, day.date);
      const startDate = formatDateForICS(eventDate);
      
      const endDate = new Date(eventDate);
      endDate.setDate(endDate.getDate() + 1);
      const endDateFormatted = formatDateForICS(endDate);

      const title = `${starRating} Moon Photography - ${day.illumination}%`;
      
      const description = 
        `🌕 OPTIMAL MOON PHOTOGRAPHY CONDITIONS` +
        `\\n\\n📍 LOCATION: ${selectedLocation.name}` +
        `\\n📐 Coordinates: ${selectedLocation.latitude.toFixed(4)}°, ${selectedLocation.longitude.toFixed(4)}°` +
        `\\n\\n🌙 Moon Phase: ${day.moonPhaseName} (${day.illumination}%)` +
        `\\n${day.isSupermoon ? '⭐ SUPERMOON - Appears 14% larger!\\n' : ''}` +
        `\\n⏰ TIMING` +
        `\\n🌙 Moonrise: ${day.moonrise} (${day.moonriseAzimuth})` +
        `\\n🌙 Moonset: ${day.moonset} (${day.moonsetAzimuth})` +
        `\\n⬆️ Max Altitude: ${day.maxAltitude}° at ${day.maxAltitudeTime}` +
        `\\n${day.twilightNote ? `\\n🌅 ${day.twilightNote}` : ''}` +
        `\\n\\n💡 PHOTOGRAPHY TIPS` +
        `\\n• Use a telephoto lens (200mm+) for detailed shots` +
        `\\n• Shoot during moonrise/moonset for dramatic compositions` +
        `\\n• Include foreground elements during twilight` +
        `\\n• Higher altitudes = less atmospheric distortion` +
        `\\n\\n📱 Plan your shoot at: https://www.milkywayplanner.com`;

      const location = escapeICSText(`${selectedLocation.name} (${selectedLocation.latitude.toFixed(4)}°, ${selectedLocation.longitude.toFixed(4)}°)`);

      const uid = `moon-${selectedYear}${String(selectedMonth + 1).padStart(2, '0')}${String(day.date).padStart(2, '0')}-${selectedLocation.latitude.toFixed(2)}${selectedLocation.longitude.toFixed(2)}@milkywayplanner.com`;
      const now = formatDateTimeForICS(new Date());

      return [
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        `DTSTART;VALUE=DATE:${startDate}`,
        `DTEND;VALUE=DATE:${endDateFormatted}`,
        `SUMMARY:${escapeICSText(title)}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        'TRANSP:TRANSPARENT',
        'END:VEVENT'
      ].join('\r\n');
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//MilkyWayPlanner//Moon Calendar//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      `X-WR-CALNAME:Moon Photography - ${selectedLocation.name}`,
      'X-WR-TIMEZONE:UTC',
      ...goodNights.map(day => createICSEvent(day)),
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setIsImportGuideOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Location Display Card - only show if not hidden by parent */}
      {!hideLocationCard && (
        <LocationDisplayCard
          location={selectedLocation}
          user={user}
          isFavoriteDialogOpen={isFavoriteDialogOpen}
          setIsFavoriteDialogOpen={setIsFavoriteDialogOpen}
          favoriteFormData={favoriteFormData}
          setFavoriteFormData={setFavoriteFormData}
          onStarLocation={handleStarLocation}
          key={`moon-${selectedLocation.latitude}-${selectedLocation.longitude}-${refreshTrigger}`}
        />
      )}

      {/* View Type Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">View:</span>
          <div className="flex gap-1">
            <Button
              variant={viewType === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('month')}
              className="flex items-center gap-1"
            >
              <CalendarDays className="w-4 h-4" />
              Monthly
            </Button>
            <Button
              variant={viewType === 'year' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('year')}
              className="flex items-center gap-1"
            >
              <LayoutGrid className="w-4 h-4" />
              Yearly
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Select 
            value={selectedYear.toString()} 
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-[100px] bg-card border-border">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {viewType === 'year' ? (
        <MoonYearlyCalendarView
          latitude={selectedLocation.latitude}
          longitude={selectedLocation.longitude}
          year={selectedYear}
          onMonthClick={(month) => {
            setSelectedMonth(month);
            setViewType('month');
          }}
        />
      ) : (
        <>
          {/* Month Tabs */}
          <Tabs 
            value={selectedMonth.toString()} 
            onValueChange={(value) => setSelectedMonth(parseInt(value))}
            className="w-full"
          >
            <TabsList className="grid grid-cols-6 md:grid-cols-12 gap-1 h-auto p-1 bg-secondary/30 rounded-lg">
              {shortMonthNames.map((month, index) => (
                <TabsTrigger
                  key={index}
                  value={index.toString()}
                  className="text-xs md:text-sm py-2 data-[state=active]:bg-foreground data-[state=active]:text-background"
                >
                  {month}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Header Card with Export Controls - ONLY FOR PAID USERS */}
          {user && isSubscribed && (
            <Card className="glass-card border-border/50 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="flex items-center text-3xl font-light text-foreground">
                      <MapPin className="w-7 h-7 mr-3 text-amber-600" />
                      {monthNames[selectedMonth]} {selectedYear} - {locationParts.city}
                      {locationParts.stateProvince && `, ${locationParts.stateProvince}`}
                    </CardTitle>
                    <div className="space-y-2 mt-4">
                      <div className="text-muted-foreground">
                        Coordinates: {selectedLocation.latitude.toFixed(4)}°, {selectedLocation.longitude.toFixed(4)}°
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Clock className="w-4 h-4 mr-2" />
                        Times in {timezone}
                      </div>
                    </div>
                  </div>
                  
                  {/* Export Controls */}
                  <div className="flex flex-col space-y-2">
                    <Button 
                      onClick={async () => {
                        trackExportDownload('pdf', selectedLocation.name);
                        toast({ title: "Generating PDF...", description: "Please wait while we create your moon planner." });
                        try {
                          const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-moon-pdf`, {
                            method: 'POST',
                            headers: await edgeFunctionHeaders(),
                            body: JSON.stringify({
                              latitude: selectedLocation.latitude,
                              longitude: selectedLocation.longitude,
                              location_name: selectedLocation.name,
                              year: selectedYear,
                              month: selectedMonth
                            })
                          });
                          if (!response.ok) throw new Error('Failed to generate PDF');
                          const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = generateFilename('pdf');
                          link.click();
                          URL.revokeObjectURL(url);
                          toast({ title: "PDF Downloaded", description: "Your moon planner has been downloaded." });
                        } catch (error) {
                          toast({ title: "Error", description: "Failed to generate PDF. Please try again.", variant: "destructive" });
                        }
                      }} 
                      variant="outline" 
                      size="sm"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      PDF
                    </Button>
                    
                    <Button onClick={exportToExcel} variant="outline" size="sm">
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Excel
                    </Button>
                    
                    <Button onClick={exportToICS} variant="outline" size="sm">
                      <Calendar className="mr-2 h-4 w-4" />
                      Export Calendar ({goodMoonNightsCount} events)
                    </Button>
                    
                    <button
                      onClick={() => setIsImportGuideOpen(true)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors underline text-left"
                    >
                      Need help importing? View import guide
                    </button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          {/* Weather Summary for Premium Users */}
          {user && isSubscribed && selectedLocation?.id && (
            <MonthlyWeatherSummary
              locationId={selectedLocation.id}
              month={selectedMonth}
              year={selectedYear}
              locationName={selectedLocation.name}
            />
          )}

          {/* How to Use This Planner */}
          <MoonHowToUse />

          {/* Best Full Moons */}
          <div>
            <BestFullMoons 
              fullMoons={bestFullMoons}
              month={selectedMonth}
              year={selectedYear}
            />
          </div>

          {/* Full Calendar Table */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-galaxy-blue" />
              Full Month Calendar
            </h2>
            <MoonCalendarTable monthData={monthData} />
          </div>

          {/* Legend */}
          <div>
            <MoonCalendarLegend />
          </div>
        </>
      )}

      {/* Calendar Import Guide Modal */}
      <CalendarImportGuideModal
        isOpen={isImportGuideOpen}
        onClose={() => setIsImportGuideOpen(false)}
        fileName={downloadedFileName}
      />
    </div>
  );
};
